import type { Ref } from 'vue'
import { computed, unref } from 'vue'
import { useI18n } from 'vue-i18n'

interface Topic {
  id: string | number
  name: string
  count?: number
}

/**
 * Composable for translating special topic names
 * Implements DRY principle for topic name translation across components
 */
export function useTopicTranslation() {
  const { t } = useI18n()

  /**
   * Translates special topic names (e.g., "Unbekannt" -> translated "Unknown")
   * @param topicName - The topic name to translate
   * @returns The translated topic name or the original if no translation needed
   */
  const getTranslatedTopicName = (topicName: string): string => {
    if (topicName?.toLowerCase() === 'unbekannt') {
      return t('labels.unknown-topic')
    }
    return topicName
  }

  /**
   * Creates a computed property that translates an array of topics
   * @param topics - The topics array (can be a ref, plain array, or function that returns an array)
   * @returns A computed property with translated topic names
   */
  const translateTopics = (topics: Topic[] | Ref<Topic[]> | (() => Topic[])) => {
    return computed(() => {
      const topicsArray = typeof topics === 'function' ? topics() : unref(topics)
      return topicsArray.map((topic) => ({
        ...topic,
        name: getTranslatedTopicName(topic.name),
      }))
    })
  }

  return {
    getTranslatedTopicName,
    translateTopics,
  }
}
